import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  SimpleGrid,
  Text,
  Spinner,
  Heading,
  Flex,
  AspectRatio,
  VStack,
  Icon,
  HStack,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import Helmet from 'react-helmet';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import EventCard from './EventCard';
import home1 from '../images/home-1.jpg';
import heroDiventaSocio from '../images/hero-diventa-socio.png';
import EventCardSkeleton from './EventCardSkeleton';

import rinnovoTesseraBg from '../images/rinnovo-tessera-bg.jpg';
import { useAuth } from '../contexts/AuthContext';
import {
  C,
  Eyebrow,
  PrimaryButton,
  TextLink,
  Section,
  SectionHead,
} from './ui/SiteKit';

/** Altezza hero mobile sotto l’header: allineato a App.js (header 56px + paddingTop 56px sul Container). */
const HOME_HERO_MOBILE_VIEW_HEIGHT = 'calc(100dvh - 56px)';

const isAbsoluteHttpUrl = (url) =>
  typeof url === 'string' && /^https?:\/\//i.test(url.trim());

/** Normalizza una slide da GET /api/home-hero-slides per il rendering hero. */
const normalizeCmsHeroSlide = (s) => {
  if (!s?.imageUrl || !s.title) return null;
  const b1Url = (s.button1Url || '').trim() || '/';
  const b2Url = (s.button2Url || '').trim();
  const slide = {
    type: 'static',
    image: s.imageUrl,
    title: s.title,
    subtitle: s.description || '',
    buttonText: (s.button1Label || '').trim() || 'Scopri',
    buttonLink: b1Url,
    external: isAbsoluteHttpUrl(b1Url) && Boolean(s.button1OpenInNewTab),
  };
  if ((s.button2Label || '').trim() && b2Url) {
    slide.button2Text = (s.button2Label || '').trim();
    slide.button2Link = b2Url;
    slide.button2External =
      isAbsoluteHttpUrl(b2Url) && Boolean(s.button2OpenInNewTab);
  }
  return slide;
};

const FALLBACK_MARKETING_HERO_SLIDES = [
  {
    type: 'static',
    image: home1,
    title: 'Scopri le attività',
    subtitle: 'Giochi, laboratori, eventi e molto altro.',
    buttonText: 'Esplora le attività',
    buttonLink: '/attivita',
  },
  {
    type: 'static',
    image: heroDiventaSocio,
    title: 'Diventa socio!',
    subtitle: 'Tesserati per accedere a tutte le iniziative esclusive.',
    buttonText: 'Tesserati ora',
    buttonLink: '/tesseramento',
  },
];

const Home = ({ eventsData }) => {
  const { userData } = useAuth();
  // Helper per formattare il prezzo dell'evento
  const formatEventPrice = (event) => {
    if (event?.Varianti?.length) {
      return event.Varianti.length === 1
        ? '1 opzione'
        : `${event.Varianti.length} opzioni`;
    }
    if (event?.prezzoOriginale) {
      return `${event.prezzo}€ - ${event.prezzoOriginale}€`;
    }
    if (event?.is_free || event?.prezzo === 0) {
      return 'Gratis';
    }
    return `€ ${event?.prezzo || 'N/A'}`;
  };

  // Helper per formattare la data dell'evento
  const formatEventDate = (event) => {
    if (!event?.Data) return null;
    const eventDate = new Date(event.Data);
    const eventEndDate = event?.Date_end ? new Date(event.Date_end) : null;

    const fullDate = eventDate.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    if (eventEndDate) {
      const endDate = eventEndDate.toLocaleDateString('it-IT', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
      return `${fullDate} - ${endDate}`;
    }

    return fullDate;
  };

  // Helper per ottenere il colore dell'evento
  const getEventColor = (event) => {
    return event?.color || '#ffcc00';
  };

  // Anno corrente e anno precedente per il rinnovo
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;

  // Funzione per verificare se mostrare la slide di rinnovo
  const shouldShowRinnovoSlide = () => {
    if (
      !userData ||
      !userData.tesseramenti ||
      !Array.isArray(userData.tesseramenti)
    ) {
      return false;
    }

    const soci = userData.tesseramenti;

    // Verifica se almeno un socio ha un tesseramento per l'anno precedente ma non per l'anno corrente
    return soci.some((socio) => {
      const tesseramentiSocio =
        socio.tesseramenti && Array.isArray(socio.tesseramenti)
          ? socio.tesseramenti
          : [];

      // Verifica se ha un tesseramento per l'anno precedente con paymentStatus succeeded
      const hasPreviousYearTesseramento = tesseramentiSocio.some(
        (t) => t.anno === previousYear && t.paymentStatus === 'succeeded'
      );

      // Verifica se NON ha un tesseramento per l'anno corrente con paymentStatus succeeded
      const hasCurrentYearTesseramento = tesseramentiSocio.some(
        (t) => t.anno === currentYear && t.paymentStatus === 'succeeded'
      );

      return hasPreviousYearTesseramento && !hasCurrentYearTesseramento;
    });
  };

  // Eventi featured per le slide hero (caricati da API dedicata)
  const [featuredEventsForHero, setFeaturedEventsForHero] = useState([]);

  // Slide marketing hero da Strapi (full CMS); se vuote si usano le due slide fallback
  const [cmsMarketingHeroSlides, setCmsMarketingHeroSlides] = useState([]);

  useEffect(() => {
    fetch('/api/home-hero-slides')
      .then((res) => res.json())
      .then((data) => {
        const raw = Array.isArray(data.slides) ? data.slides : [];
        const normalized = raw.map(normalizeCmsHeroSlide).filter(Boolean);
        setCmsMarketingHeroSlides(normalized);
      })
      .catch(() => {
        setCmsMarketingHeroSlides([]);
      });
  }, []);

  // Crea slide per gli eventi featured
  const eventSlides = featuredEventsForHero.map((event) => ({
    type: 'event',
    event,
    image: event?.thumbnail?.url || home1,
    title: event?.Nome || 'Evento',
    subtitle: formatEventDate(event),
    color: getEventColor(event),
    price: formatEventPrice(event),
    location: event?.Location,
    partecipanti: event?.Partecipanti,
    oraInizio: event?.Ora_inizio
      ? event.Ora_inizio.split(':').slice(0, 2).join(':')
      : null,
    buttonText: 'Scopri di più',
    buttonLink: `/event/${event?.urlTitle || ''}`,
  }));

  // Slide di rinnovo tessera (se applicabile)
  const rinnovoSlide = shouldShowRinnovoSlide()
    ? [
        {
          type: 'static',
          image: rinnovoTesseraBg,
          title: `Rinnova la tessera ${currentYear}!`,
          subtitle:
            'Non perdere i vantaggi della tua tessera CIL! Rinnova ora per continuare a partecipare a tutte le attività esclusive.',
          buttonText: 'Rinnova ora',
          buttonLink: '/account/tesseramenti',
        },
      ]
    : [];

  const marketingHeroSlides =
    cmsMarketingHeroSlides.length > 0
      ? cmsMarketingHeroSlides
      : FALLBACK_MARKETING_HERO_SLIDES;

  // Slides dell'hero: marketing da CMS (o fallback), poi rinnovo tessera se applicabile, poi eventi featured
  const heroSlides = [...marketingHeroSlides, ...rinnovoSlide, ...eventSlides];

  // Stato per controllare l'indice del current slide
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!heroSlides.length) return;
    setCurrentSlide((prev) => (prev >= heroSlides.length ? 0 : prev));
  }, [heroSlides.length]);

  // Effetto per cambiare slide ogni 5 secondi
  useEffect(() => {
    if (!heroSlides.length) return undefined;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroSlides.length]);

  // Stato per gli eventi della home (caricati dal server)
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loadingHomeEvents, setLoadingHomeEvents] = useState(true);

  // Helper per verificare se un evento è futuro
  const isFutureEvent = (event) => {
    if (!event?.Data) return false;
    const now = new Date();
    const eventDate = new Date(event.Data);
    const eventEndDate = event?.Date_end ? new Date(event.Date_end) : null;

    // Se ha una data di fine, controlla quella, altrimenti controlla la data di inizio
    const referenceDate = eventEndDate || eventDate;
    return referenceDate > now;
  };

  // Fetch eventi featured per le slide hero (endpoint dedicato)
  useEffect(() => {
    fetch('/api/eventi/featured')
      .then((res) => res.json())
      .then((data) => {
        setFeaturedEventsForHero(data.events || []);
      })
      .catch((err) => {
        console.error('Error fetching featured events:', err);
        setFeaturedEventsForHero([]);
      });
  }, []);

  // Fetch eventi ottimizzati per la home dal server
  useEffect(() => {
    fetch('/api/eventi/home')
      .then((res) => res.json())
      .then((data) => {
        // Filtra solo eventi futuri per assicurarsi che non vengano mostrati eventi passati
        const futureEvents = (data.events || [])
          .filter(isFutureEvent)
          .sort((a, b) => new Date(a.Data) - new Date(b.Data));
        setUpcomingEvents(futureEvents);
        setLoadingHomeEvents(false);
      })
      .catch((err) => {
        console.error('Error fetching home events:', err);
        setLoadingHomeEvents(false);
        // Fallback: usa eventsData se l'endpoint fallisce
        const futureEvents = (eventsData || [])
          .filter(isFutureEvent)
          .sort((a, b) => new Date(a.Data) - new Date(b.Data));
        setUpcomingEvents(futureEvents.slice(0, 6));
      });
  }, []);

  // Stato per i dati delle statistiche
  const [stats, setStats] = useState({
    totalMembers: 0,
    totalEvents: 0,
    availableEvents: 0,
    minorsCount: 0,
    adultsCount: 0,
  });
  const [loading, setLoading] = useState(true);

  // Effetto "count-up" manuale
  const animateCountUp = (targetValue, setter, duration = 2000) => {
    let start = 0;
    const increment = Math.ceil(targetValue / (duration / 50));
    const interval = setInterval(() => {
      start += increment;
      if (start >= targetValue) {
        start = targetValue;
        clearInterval(interval);
      }
      setter(start);
    }, 50);
  };

  // Fetch dati counters
  useEffect(() => {
    fetch('/api/statistics')
      .then((res) => res.json())
      .then((data) => {
        setLoading(false);
        animateCountUp(data.totalMembers || 0, (val) =>
          setStats((prev) => ({ ...prev, totalMembers: val }))
        );
        animateCountUp(data.totalEvents || 0, (val) =>
          setStats((prev) => ({ ...prev, totalEvents: val }))
        );
        animateCountUp(data.availableEvents || 0, (val) =>
          setStats((prev) => ({ ...prev, availableEvents: val }))
        );
        animateCountUp(data.minorsCount || 0, (val) =>
          setStats((prev) => ({ ...prev, minorsCount: val }))
        );
        animateCountUp(data.adultsCount || 0, (val) =>
          setStats((prev) => ({ ...prev, adultsCount: val }))
        );
      })
      .catch((err) => {
        console.error('Errore fetch statistiche:', err);
        setLoading(false);
      });
  }, []);

  const upcomingEventsForHome = upcomingEvents.slice(0, 6);
  const upcomingEventsCarouselCount = upcomingEventsForHome.length;
  const upcomingEventsCarouselSettings = {
    dots: true,
    infinite: upcomingEventsCarouselCount > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: upcomingEventsCarouselCount > 1,
    autoplaySpeed: 5000,
    arrows: false,
    adaptiveHeight: true,
    pauseOnHover: true,
  };

  const pct = (n) =>
    stats.totalMembers > 0 ? Math.round((n / stats.totalMembers) * 100) : 0;
  const adultsPct = pct(stats.adultsCount);
  const minorsPct = pct(stats.minorsCount);

  const heroLinkProps = (external, link) =>
    external
      ? { as: 'a', href: link, target: '_blank', rel: 'noopener noreferrer' }
      : { as: Link, to: link };

  return (
    <>
      <Helmet>
        <title>CIL - Creatività in Libertà</title>
        <meta property="og:title" content="CIL - Creatività in Libertà" />
        <meta
          property="og:description"
          content="CIL - Creatività in Libertà. Scopri giochi, laboratori, eventi e molto altro. Un'associazione che promuove creatività, gioco, partecipazione e cura della comunità."
        />
        <meta
          property="og:image"
          content={`${window.location.origin}/logo.png`}
        />
        <meta property="og:url" content={`${window.location.origin}/`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CIL - Creatività in Libertà" />
        <meta
          name="twitter:description"
          content="CIL - Creatività in Libertà. Scopri giochi, laboratori, eventi e molto altro. Un'associazione che promuove creatività, gioco, partecipazione e cura della comunità."
        />
        <meta
          name="twitter:image"
          content={`${window.location.origin}/logo.png`}
        />
      </Helmet>
      <Box bg={C.bg} minH="100vh" overflowX="hidden" color="white">
        {/* HERO: testo a sinistra, 1 CTA + 1 link, indicatori a segmenti */}
        <Box
          position="relative"
          w="100%"
          h={{ base: HOME_HERO_MOBILE_VIEW_HEIGHT, md: '520px' }}
          minH={{ base: HOME_HERO_MOBILE_VIEW_HEIGHT, md: '520px' }}
          overflow="hidden"
          bg={C.surface}
        >
          {heroSlides.map((slide, index) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              position="absolute"
              inset={0}
              backgroundImage={`url('${slide.image}')`}
              backgroundSize="cover"
              backgroundPosition="center"
              transition="opacity 0.8s ease-in-out"
              opacity={index === currentSlide ? 1 : 0}
              zIndex={index === currentSlide ? 1 : 0}
              pointerEvents={index === currentSlide ? 'auto' : 'none'}
            >
              <Box
                position="absolute"
                inset={0}
                bg={{
                  base: 'linear-gradient(0deg, rgba(14,16,19,0.92) 0%, rgba(14,16,19,0.55) 60%, rgba(14,16,19,0.25) 100%)',
                  md: 'linear-gradient(90deg, rgba(14,16,19,0.92) 0%, rgba(14,16,19,0.6) 45%, rgba(14,16,19,0.1) 100%)',
                }}
              />
              <Container
                maxW="1120px"
                h="100%"
                display="flex"
                flexDirection="column"
                justifyContent="flex-end"
                position="relative"
                zIndex={2}
                px={{ base: 4, md: 8 }}
                pt={{ base: 12, md: 18 }}
                pb={{ base: 20, md: 20 }}
              >
                <Eyebrow>
                  {slide.type === 'event'
                    ? [slide.subtitle, slide.oraInizio]
                        .filter(Boolean)
                        .join(' · ') || 'In evidenza'
                    : 'Associazione culturale'}
                </Eyebrow>
                <Heading
                  as="h2"
                  fontSize={{ base: '4xl', md: '6xl' }}
                  fontWeight="800"
                  lineHeight="1.05"
                  letterSpacing="-0.02em"
                  maxW="16ch"
                  mb={4}
                >
                  {slide.title}
                </Heading>
                <Text
                  fontSize={{ base: 'md', md: 'lg' }}
                  color="gray.200"
                  maxW="46ch"
                  mb={8}
                >
                  {slide.type === 'event'
                    ? [slide.price, slide.partecipanti, slide.location]
                        .filter(Boolean)
                        .join(' · ')
                    : slide.subtitle}
                </Text>
                <HStack spacing={8} flexWrap="wrap" rowGap={4}>
                  <PrimaryButton
                    {...heroLinkProps(slide.external, slide.buttonLink)}
                  >
                    {slide.buttonText}
                  </PrimaryButton>
                  {slide.button2Text && slide.button2Link ? (
                    <TextLink
                      {...heroLinkProps(
                        slide.button2External,
                        slide.button2Link
                      )}
                    >
                      {slide.button2Text}
                    </TextLink>
                  ) : null}
                </HStack>
              </Container>
            </Box>
          ))}
          {heroSlides.length > 1 && (
            <Container
              maxW="1120px"
              position="absolute"
              left={0}
              right={0}
              bottom={{ base: 6, md: 8 }}
              zIndex={3}
              px={{ base: 4, md: 8 }}
            >
              <HStack spacing={2}>
                {heroSlides.map((slide, index) => (
                  <Box
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    as="button"
                    type="button"
                    aria-label={`Vai alla slide ${index + 1}`}
                    onClick={() => setCurrentSlide(index)}
                    w={{ base: '28px', md: '40px' }}
                    h="3px"
                    borderRadius="2px"
                    bg={
                      index === currentSlide
                        ? C.yellow
                        : 'rgba(255,255,255,0.28)'
                    }
                    transition="background 0.3s"
                  />
                ))}
              </HStack>
            </Container>
          )}
        </Box>

        {/* PROSSIMI EVENTI */}
        <Section>
          <SectionHead
            eyebrow="Eventi"
            title="Prossimi appuntamenti"
            action={
              <TextLink as={Link} to="/attivita">
                Vedi tutti
              </TextLink>
            }
          />
          {loadingHomeEvents ? (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              <EventCardSkeleton index={1} />
              <Box display={{ base: 'none', md: 'block' }}>
                <EventCardSkeleton index={2} />
              </Box>
              <Box display={{ base: 'none', lg: 'block' }}>
                <EventCardSkeleton index={3} />
              </Box>
            </SimpleGrid>
          ) : upcomingEventsForHome.length > 0 ? (
            <>
              <Box
                display={{ base: 'block', md: 'none' }}
                w="100%"
                sx={{
                  '.slick-dots li button:before': {
                    color: 'rgba(255,255,255,0.45)',
                  },
                  '.slick-dots li.slick-active button:before': {
                    color: C.yellow,
                  },
                }}
              >
                <Slider {...upcomingEventsCarouselSettings}>
                  {upcomingEventsForHome.map((event, index) => (
                    <Box key={event.documentId} px={2} py={1}>
                      <EventCard event={event} index={index} />
                    </Box>
                  ))}
                </Slider>
              </Box>
              <SimpleGrid
                display={{ base: 'none', md: 'grid' }}
                columns={{ md: 2, lg: 3 }}
                spacing={6}
              >
                {upcomingEventsForHome.map((event, index) => (
                  <EventCard
                    key={event.documentId}
                    event={event}
                    index={index}
                  />
                ))}
              </SimpleGrid>
            </>
          ) : (
            <Text color="gray.400" py={8}>
              Nessun evento disponibile al momento
            </Text>
          )}
        </Section>

        {/* CHI SIAMO + NUMERI */}
        <Section flush>
          <Grid
            templateColumns={{ base: '1fr', lg: '1.05fr 1fr' }}
            gap={{ base: 8, lg: 16 }}
            alignItems="center"
          >
            <Box>
              <Eyebrow>Chi siamo</Eyebrow>
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                fontWeight="800"
                letterSpacing="-0.02em"
                mb={4}
              >
                CIL – Creatività in Libertà
              </Heading>
              <Text color="gray.400" fontSize="lg" maxW="56ch" mb={6}>
                Un&apos;associazione culturale nata per offrire uno spazio dove{' '}
                <Text as="span" color={C.yellow} fontWeight="700">
                  gioco, arte e creatività
                </Text>{' '}
                si incontrano. Eventi, laboratori e iniziative per tutte le età,
                in un ambiente aperto e inclusivo.
              </Text>
              <TextLink as={Link} to="/page/about-us">
                Scopri di più
              </TextLink>
            </Box>
            <Box
              borderRadius="14px"
              overflow="hidden"
              border="1px solid"
              borderColor={C.line}
              bg={C.surface}
            >
              <AspectRatio ratio={16 / 9} w="100%">
                <iframe
                  title="Video Presentazione CIL"
                  src="https://www.youtube.com/embed/fDnnIzcOcvE?autoplay=1&mute=1&loop=1&playlist=fDnnIzcOcvE&controls=0&modestbranding=1&rel=0&iv_load_policy=3&disablekb=1&fs=0&playsinline=1"
                  allow="autoplay; encrypted-media; fullscreen; loop"
                  style={{ pointerEvents: 'none' }}
                />
              </AspectRatio>
            </Box>
          </Grid>

          {/* Fascia numeri unica */}
          <Grid
            mt={12}
            templateColumns={{
              base: 'repeat(3, 1fr)',
              lg: 'repeat(3, 1fr) 1.6fr',
            }}
            border="1px solid"
            borderColor={C.line}
            borderRadius="14px"
            bg={C.surface}
            overflow="hidden"
          >
            {[
              ['Soci tesserati', stats.totalMembers],
              ['Eventi organizzati', stats.totalEvents],
              ['Eventi disponibili', stats.availableEvents],
            ].map(([label, value], i) => (
              <Box
                key={label}
                p={{ base: 4, md: 6 }}
                borderRight={{
                  base: i < 2 ? '1px solid' : 'none',
                  lg: '1px solid',
                }}
                borderColor={C.line}
              >
                {loading ? (
                  <Spinner size="sm" color={C.yellow} />
                ) : (
                  <Text
                    fontSize={{ base: '2xl', md: '4xl' }}
                    fontWeight="800"
                    letterSpacing="-0.03em"
                    lineHeight={1}
                  >
                    {value}
                  </Text>
                )}
                <Text
                  mt={1.5}
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight="600"
                  color="gray.400"
                >
                  {label}
                </Text>
              </Box>
            ))}
            <Flex
              gridColumn={{ base: '1 / -1', lg: 'auto' }}
              borderTop={{ base: '1px solid', lg: 'none' }}
              borderColor={C.line}
              direction="column"
              justify="center"
              gap={2.5}
              p={{ base: 4, md: 6 }}
            >
              <Flex h="8px" borderRadius="full" overflow="hidden" bg="#252930">
                <Box
                  w={`${adultsPct}%`}
                  bg="#f08a3c"
                  transition="width 0.8s ease-in-out"
                />
                <Box
                  w={`${minorsPct}%`}
                  bg="#36a9e1"
                  transition="width 0.8s ease-in-out"
                />
              </Flex>
              <Flex
                justify="space-between"
                fontSize="sm"
                fontWeight="600"
                color="gray.400"
              >
                <HStack spacing={1.5}>
                  <Box w="8px" h="8px" borderRadius="full" bg="#f08a3c" />
                  <span>
                    Adulti{' '}
                    <Text as="b" color="white">
                      {adultsPct}%
                    </Text>
                  </span>
                </HStack>
                <HStack spacing={1.5}>
                  <Box w="8px" h="8px" borderRadius="full" bg="#36a9e1" />
                  <span>
                    Minorenni{' '}
                    <Text as="b" color="white">
                      {minorsPct}%
                    </Text>
                  </span>
                </HStack>
              </Flex>
            </Flex>
          </Grid>
        </Section>

        {/* UNISCITI: socio (primaria) + sostienici (secondaria) */}
        <Section flush>
          <Grid templateColumns={{ base: '1fr', lg: '1.2fr 1fr' }} gap={6}>
            <Flex
              direction="column"
              align="flex-start"
              gap={4}
              bg={C.yellow}
              color={C.bg}
              borderRadius="20px"
              p={{ base: 6, md: 12 }}
            >
              <Eyebrow color="rgba(22,24,29,0.65)" mb={0}>
                Tesseramento
              </Eyebrow>
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                fontWeight="800"
                letterSpacing="-0.02em"
              >
                Diventa socio di CIL
              </Heading>
              <VStack align="start" spacing={2} fontWeight="700" my={1}>
                {[
                  'Accesso a eventi e tornei esclusivi',
                  'Laboratori creativi per tutte le età',
                  'Una community di giocatori e creativi',
                ].map((t) => (
                  <HStack key={t} spacing={2.5}>
                    <Icon as={FaCheck} boxSize={4} />
                    <span>{t}</span>
                  </HStack>
                ))}
              </VStack>
              <PrimaryButton
                as={Link}
                to="/tesseramento"
                bg={C.bg}
                color={C.yellow}
                _hover={{ bg: '#000', transform: 'translateY(-1px)' }}
              >
                Tesserati ora
              </PrimaryButton>
            </Flex>

            <Flex
              direction="column"
              align="flex-start"
              gap={4}
              bg={C.surface}
              border="1px solid"
              borderColor={C.line}
              borderRadius="20px"
              p={{ base: 6, md: 12 }}
            >
              <Eyebrow mb={0}>Sostienici</Eyebrow>
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                fontWeight="800"
                letterSpacing="-0.02em"
              >
                Tutto questo grazie a voi
              </Heading>
              <Text color="gray.400" maxW="46ch">
                Le attività, la sede e l&apos;amministrazione di CIL sono
                portate avanti solo dall&apos;impegno dei soci. Il tuo sostegno
                fa la differenza.
              </Text>
              <Box mt="auto">
                <TextLink
                  as={Link}
                  to="/sostienicil"
                  className="homepage-cta-sostienicil"
                >
                  Dona o diventa Sostenitore
                </TextLink>
              </Box>
            </Flex>
          </Grid>
        </Section>
      </Box>
    </>
  );
};

export default Home;
